import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import {
  supportedLocales,
  type SupportedLocale,
} from './types/simulation'
import { resolveSupportedLocale, uiMessages } from './i18n/ui'

const en = {
  common: {
    loading: 'Loading',
  },
  nav: {
    simulate: 'Community',
    trending: 'Trending',
    library: 'My Library',
    created: 'My Creations',
    collections: 'Collections',
    following: 'Following',
    profile: 'Profile',
    search: 'Search',
    costs: 'Cost Console',
  },
  prompt: {
    placeholder: 'What do you want to simulate?',
    badge: 'Live experiment with Gemini',
    reviewingQuestion: 'Reviewing the question',
    preparingSimulation: 'Preparing the simulation',
    definingDetails: 'Identifying the details needed for a strong visual model.',
    buildingExperience: 'Building the scene, interactions, model, and sources together.',
    lockedDuringWork: 'This window stays open until the process finishes; then we will continue automatically.',
    generationFailedTitle: 'The simulation could not be created',
    generationFailedBody: 'Your credit was returned. You can try again with the same question.',
    generationFailedAction: 'Try again',
    liveUnavailable: 'Live creation is temporarily unavailable. Please try again later.',
    loginRequired: 'Sign in to create a new simulation.',
    loginAction: 'Sign in',
  },
}

const tr: typeof en = {
  common: {
    loading: 'Yükleniyor',
  },
  nav: {
    simulate: 'Topluluk',
    trending: 'Trending',
    library: 'Kitaplık',
    created: 'Oluşturduklarım',
    collections: 'Koleksiyonlar',
    following: 'Takip',
    profile: 'Profil',
    search: 'Ara',
    costs: 'Maliyet Konsolu',
  },
  prompt: {
    placeholder: 'Neyi simüle etmek istiyorsun?',
    badge: 'Gemini ile canlı deney',
    reviewingQuestion: 'Soru inceleniyor',
    preparingSimulation: 'Simülasyon hazırlanıyor',
    definingDetails: 'Güçlü bir görsel model için gerekli ayrıntılar belirleniyor.',
    buildingExperience: 'Sahne, etkileşimler, model ve kaynaklar birlikte oluşturuluyor.',
    lockedDuringWork: 'İşlem tamamlanana kadar bu pencere açık kalır; ardından otomatik olarak devam ederiz.',
    generationFailedTitle: 'Simülasyon oluşturulamadı',
    generationFailedBody: 'Kredin iade edildi. Aynı soruyla yeniden deneyebilirsin.',
    generationFailedAction: 'Yeniden dene',
    liveUnavailable: 'Canlı üretim geçici olarak kullanılamıyor. Lütfen daha sonra yeniden dene.',
    loginRequired: 'Yeni bir simülasyon oluşturmak için giriş yapmalısın.',
    loginAction: 'Giriş yap',
  },
}

const loadingByLocale: Record<SupportedLocale, string> = {
  en: 'Loading',
  tr: 'Yükleniyor',
  de: 'Wird geladen',
  es: 'Cargando',
  fr: 'Chargement',
  ru: 'Загрузка',
  it: 'Caricamento',
  pt: 'Carregando',
  sv: 'Laddar',
  nl: 'Laden',
  ar: 'جارٍ التحميل',
  hi: 'लोड हो रहा है',
  id: 'Memuat',
  ja: '読み込み中',
  ko: '불러오는 중',
  pl: 'Ładowanie',
  th: 'กำลังโหลด',
}

const promptProgressByLocale: Record<
  SupportedLocale,
  Pick<typeof en.prompt, 'reviewingQuestion' | 'preparingSimulation' | 'definingDetails' | 'buildingExperience' | 'lockedDuringWork'>
> = {
  en: en.prompt,
  tr: tr.prompt,
  de: {
    reviewingQuestion: 'Frage wird geprüft',
    preparingSimulation: 'Simulation wird vorbereitet',
    definingDetails: 'Die Details für ein aussagekräftiges visuelles Modell werden bestimmt.',
    buildingExperience: 'Szene, Interaktionen, Modell und Quellen werden gemeinsam erstellt.',
    lockedDuringWork: 'Dieses Fenster bleibt bis zum Abschluss geöffnet; danach geht es automatisch weiter.',
  },
  es: {
    reviewingQuestion: 'Analizando la pregunta',
    preparingSimulation: 'Preparando la simulación',
    definingDetails: 'Se están definiendo los detalles necesarios para un modelo visual sólido.',
    buildingExperience: 'La escena, las interacciones, el modelo y las fuentes se crean en conjunto.',
    lockedDuringWork: 'Esta ventana permanecerá abierta hasta que termine el proceso; después continuaremos automáticamente.',
  },
  fr: {
    reviewingQuestion: 'Analyse de la question',
    preparingSimulation: 'Préparation de la simulation',
    definingDetails: 'Les détails nécessaires à un modèle visuel solide sont en cours de définition.',
    buildingExperience: 'La scène, les interactions, le modèle et les sources sont créés ensemble.',
    lockedDuringWork: 'Cette fenêtre reste ouverte jusqu’à la fin du traitement, puis nous continuerons automatiquement.',
  },
  ru: {
    reviewingQuestion: 'Анализируем вопрос',
    preparingSimulation: 'Готовим симуляцию',
    definingDetails: 'Определяем детали, необходимые для качественной визуальной модели.',
    buildingExperience: 'Сцена, взаимодействия, модель и источники создаются вместе.',
    lockedDuringWork: 'Окно останется открытым до завершения процесса, затем работа продолжится автоматически.',
  },
  it: {
    reviewingQuestion: 'Analisi della domanda',
    preparingSimulation: 'Preparazione della simulazione',
    definingDetails: 'Stiamo definendo i dettagli necessari per un modello visivo efficace.',
    buildingExperience: 'Scena, interazioni, modello e fonti vengono creati insieme.',
    lockedDuringWork: 'Questa finestra resterà aperta fino al termine; poi continueremo automaticamente.',
  },
  pt: {
    reviewingQuestion: 'Analisando a pergunta',
    preparingSimulation: 'Preparando a simulação',
    definingDetails: 'Estamos definindo os detalhes necessários para um modelo visual consistente.',
    buildingExperience: 'Cena, interações, modelo e fontes estão sendo criados em conjunto.',
    lockedDuringWork: 'Esta janela permanecerá aberta até o processo terminar; depois continuaremos automaticamente.',
  },
  sv: {
    reviewingQuestion: 'Frågan granskas',
    preparingSimulation: 'Simuleringen förbereds',
    definingDetails: 'Detaljerna som behövs för en tydlig visuell modell identifieras.',
    buildingExperience: 'Scen, interaktioner, modell och källor skapas tillsammans.',
    lockedDuringWork: 'Fönstret förblir öppet tills processen är klar och fortsätter sedan automatiskt.',
  },
  nl: {
    reviewingQuestion: 'Vraag wordt beoordeeld',
    preparingSimulation: 'Simulatie wordt voorbereid',
    definingDetails: 'De details voor een sterk visueel model worden bepaald.',
    buildingExperience: 'Scène, interacties, model en bronnen worden samen opgebouwd.',
    lockedDuringWork: 'Dit venster blijft open tot het proces klaar is; daarna gaan we automatisch verder.',
  },
  ar: {
    reviewingQuestion: 'جارٍ تحليل السؤال',
    preparingSimulation: 'جارٍ إعداد المحاكاة',
    definingDetails: 'يتم تحديد التفاصيل اللازمة لبناء نموذج بصري واضح.',
    buildingExperience: 'يتم إنشاء المشهد والتفاعلات والنموذج والمصادر معًا.',
    lockedDuringWork: 'ستبقى هذه النافذة مفتوحة حتى اكتمال العملية، ثم سنتابع تلقائيًا.',
  },
  hi: {
    reviewingQuestion: 'प्रश्न की समीक्षा हो रही है',
    preparingSimulation: 'सिमुलेशन तैयार हो रहा है',
    definingDetails: 'एक प्रभावी दृश्य मॉडल के लिए आवश्यक विवरण तय किए जा रहे हैं।',
    buildingExperience: 'दृश्य, इंटरैक्शन, मॉडल और स्रोत एक साथ बनाए जा रहे हैं।',
    lockedDuringWork: 'प्रक्रिया पूरी होने तक यह विंडो खुली रहेगी; फिर हम अपने-आप आगे बढ़ेंगे।',
  },
  id: {
    reviewingQuestion: 'Meninjau pertanyaan',
    preparingSimulation: 'Menyiapkan simulasi',
    definingDetails: 'Detail yang diperlukan untuk model visual yang kuat sedang ditentukan.',
    buildingExperience: 'Adegan, interaksi, model, dan sumber dibuat bersama.',
    lockedDuringWork: 'Jendela ini tetap terbuka sampai proses selesai, lalu berlanjut otomatis.',
  },
  ja: {
    reviewingQuestion: '質問を確認しています',
    preparingSimulation: 'シミュレーションを準備しています',
    definingDetails: '分かりやすい視覚モデルに必要な詳細を整理しています。',
    buildingExperience: 'シーン、操作、モデル、出典をまとめて作成しています。',
    lockedDuringWork: '処理が完了するまでこの画面は開いたままになり、その後自動的に続行します。',
  },
  ko: {
    reviewingQuestion: '질문을 검토하고 있습니다',
    preparingSimulation: '시뮬레이션을 준비하고 있습니다',
    definingDetails: '명확한 시각 모델에 필요한 세부 정보를 정리하고 있습니다.',
    buildingExperience: '장면, 상호작용, 모델, 출처를 함께 만들고 있습니다.',
    lockedDuringWork: '작업이 끝날 때까지 이 창이 열린 상태로 유지되며 이후 자동으로 계속됩니다.',
  },
  pl: {
    reviewingQuestion: 'Analizowanie pytania',
    preparingSimulation: 'Przygotowywanie symulacji',
    definingDetails: 'Ustalamy szczegóły potrzebne do stworzenia dobrego modelu wizualnego.',
    buildingExperience: 'Scena, interakcje, model i źródła powstają wspólnie.',
    lockedDuringWork: 'Okno pozostanie otwarte do zakończenia procesu, a potem przejdziemy dalej automatycznie.',
  },
  th: {
    reviewingQuestion: 'กำลังตรวจสอบคำถาม',
    preparingSimulation: 'กำลังเตรียมการจำลอง',
    definingDetails: 'กำลังกำหนดรายละเอียดที่จำเป็นสำหรับแบบจำลองภาพที่ชัดเจน',
    buildingExperience: 'กำลังสร้างฉาก การโต้ตอบ แบบจำลอง และแหล่งข้อมูลร่วมกัน',
    lockedDuringWork: 'หน้าต่างนี้จะเปิดอยู่จนกว่ากระบวนการจะเสร็จ แล้วระบบจะดำเนินการต่อโดยอัตโนมัติ',
  },
}

const promptErrorByLocale: Record<
  SupportedLocale,
  Pick<
    typeof en.prompt,
    | 'generationFailedTitle'
    | 'generationFailedBody'
    | 'generationFailedAction'
    | 'liveUnavailable'
    | 'loginRequired'
  >
> = {
  en: en.prompt,
  tr: tr.prompt,
  de: {
    generationFailedTitle: 'Die Simulation konnte nicht erstellt werden',
    generationFailedBody: 'Dein Guthaben wurde zurückgebucht. Du kannst es mit derselben Frage erneut versuchen.',
    generationFailedAction: 'Erneut versuchen',
    liveUnavailable: 'Die Live-Erstellung ist vorübergehend nicht verfügbar. Bitte versuche es später erneut.',
    loginRequired: 'Melde dich an, um eine neue Simulation zu erstellen.',
  },
  es: {
    generationFailedTitle: 'No se pudo crear la simulación',
    generationFailedBody: 'Tu crédito fue devuelto. Puedes intentarlo de nuevo con la misma pregunta.',
    generationFailedAction: 'Intentar de nuevo',
    liveUnavailable: 'La creación en vivo no está disponible temporalmente. Inténtalo más tarde.',
    loginRequired: 'Inicia sesión para crear una nueva simulación.',
  },
  fr: {
    generationFailedTitle: 'La simulation n’a pas pu être créée',
    generationFailedBody: 'Votre crédit a été restitué. Vous pouvez réessayer avec la même question.',
    generationFailedAction: 'Réessayer',
    liveUnavailable: 'La création en direct est temporairement indisponible. Réessayez plus tard.',
    loginRequired: 'Connectez-vous pour créer une nouvelle simulation.',
  },
  ru: {
    generationFailedTitle: 'Не удалось создать симуляцию',
    generationFailedBody: 'Кредит возвращён. Можно повторить попытку с тем же вопросом.',
    generationFailedAction: 'Попробовать снова',
    liveUnavailable: 'Создание временно недоступно. Повторите попытку позже.',
    loginRequired: 'Войдите, чтобы создать новую симуляцию.',
  },
  it: {
    generationFailedTitle: 'Impossibile creare la simulazione',
    generationFailedBody: 'Il credito è stato restituito. Puoi riprovare con la stessa domanda.',
    generationFailedAction: 'Riprova',
    liveUnavailable: 'La creazione live non è temporaneamente disponibile. Riprova più tardi.',
    loginRequired: 'Accedi per creare una nuova simulazione.',
  },
  pt: {
    generationFailedTitle: 'Não foi possível criar a simulação',
    generationFailedBody: 'Seu crédito foi devolvido. Você pode tentar novamente com a mesma pergunta.',
    generationFailedAction: 'Tentar novamente',
    liveUnavailable: 'A criação ao vivo está temporariamente indisponível. Tente novamente mais tarde.',
    loginRequired: 'Entre para criar uma nova simulação.',
  },
  sv: {
    generationFailedTitle: 'Simuleringen kunde inte skapas',
    generationFailedBody: 'Din kredit har återförts. Du kan försöka igen med samma fråga.',
    generationFailedAction: 'Försök igen',
    liveUnavailable: 'Direktskapande är tillfälligt otillgängligt. Försök igen senare.',
    loginRequired: 'Logga in för att skapa en ny simulering.',
  },
  nl: {
    generationFailedTitle: 'De simulatie kon niet worden gemaakt',
    generationFailedBody: 'Je tegoed is teruggestort. Je kunt het opnieuw proberen met dezelfde vraag.',
    generationFailedAction: 'Opnieuw proberen',
    liveUnavailable: 'Live maken is tijdelijk niet beschikbaar. Probeer het later opnieuw.',
    loginRequired: 'Log in om een nieuwe simulatie te maken.',
  },
  ar: {
    generationFailedTitle: 'تعذر إنشاء المحاكاة',
    generationFailedBody: 'تمت إعادة رصيدك. يمكنك المحاولة مجددًا بالسؤال نفسه.',
    generationFailedAction: 'المحاولة مجددًا',
    liveUnavailable: 'الإنشاء المباشر غير متاح مؤقتًا. يرجى المحاولة لاحقًا.',
    loginRequired: 'سجّل الدخول لإنشاء محاكاة جديدة.',
  },
  hi: {
    generationFailedTitle: 'सिमुलेशन नहीं बनाया जा सका',
    generationFailedBody: 'आपका क्रेडिट वापस कर दिया गया है। आप उसी प्रश्न के साथ फिर प्रयास कर सकते हैं।',
    generationFailedAction: 'फिर प्रयास करें',
    liveUnavailable: 'लाइव निर्माण अस्थायी रूप से उपलब्ध नहीं है। कृपया बाद में फिर प्रयास करें।',
    loginRequired: 'नया सिमुलेशन बनाने के लिए साइन इन करें।',
  },
  id: {
    generationFailedTitle: 'Simulasi tidak dapat dibuat',
    generationFailedBody: 'Kredit Anda telah dikembalikan. Anda dapat mencoba lagi dengan pertanyaan yang sama.',
    generationFailedAction: 'Coba lagi',
    liveUnavailable: 'Pembuatan langsung sementara tidak tersedia. Coba lagi nanti.',
    loginRequired: 'Masuk untuk membuat simulasi baru.',
  },
  ja: {
    generationFailedTitle: 'シミュレーションを作成できませんでした',
    generationFailedBody: 'クレジットは返却されました。同じ質問でもう一度お試しいただけます。',
    generationFailedAction: 'もう一度試す',
    liveUnavailable: 'ライブ作成は一時的に利用できません。後でもう一度お試しください。',
    loginRequired: '新しいシミュレーションを作成するにはログインしてください。',
  },
  ko: {
    generationFailedTitle: '시뮬레이션을 만들 수 없습니다',
    generationFailedBody: '크레딧이 반환되었습니다. 같은 질문으로 다시 시도할 수 있습니다.',
    generationFailedAction: '다시 시도',
    liveUnavailable: '실시간 생성 기능을 일시적으로 사용할 수 없습니다. 나중에 다시 시도해 주세요.',
    loginRequired: '새 시뮬레이션을 만들려면 로그인하세요.',
  },
  pl: {
    generationFailedTitle: 'Nie udało się utworzyć symulacji',
    generationFailedBody: 'Kredyt został zwrócony. Możesz spróbować ponownie z tym samym pytaniem.',
    generationFailedAction: 'Spróbuj ponownie',
    liveUnavailable: 'Tworzenie na żywo jest tymczasowo niedostępne. Spróbuj ponownie później.',
    loginRequired: 'Zaloguj się, aby utworzyć nową symulację.',
  },
  th: {
    generationFailedTitle: 'ไม่สามารถสร้างการจำลองได้',
    generationFailedBody: 'คืนเครดิตให้คุณแล้ว คุณสามารถลองอีกครั้งด้วยคำถามเดิม',
    generationFailedAction: 'ลองอีกครั้ง',
    liveUnavailable: 'การสร้างแบบสดไม่พร้อมใช้งานชั่วคราว โปรดลองอีกครั้งภายหลัง',
    loginRequired: 'ลงชื่อเข้าใช้เพื่อสร้างการจำลองใหม่',
  },
}

const resources = Object.fromEntries(
  supportedLocales.map((locale) => [
    locale,
    {
      translation: {
        ...(locale === 'tr' ? tr : en),
        ui: uiMessages[locale],
        common: {
          ...(locale === 'tr' ? tr.common : en.common),
          loading: loadingByLocale[locale],
        },
        prompt: {
          ...(locale === 'tr' ? tr.prompt : en.prompt),
          ...promptProgressByLocale[locale],
          ...promptErrorByLocale[locale],
        },
      },
    },
  ]),
) as unknown as Record<SupportedLocale, { translation: typeof en }>

void i18n.use(initReactI18next).init({
  resources,
  supportedLngs: [...supportedLocales],
  lng: resolveSupportedLocale(localStorage.getItem('voxnela:locale') ?? navigator.language),
  fallbackLng: 'en',
  showSupportNotice: false,
  interpolation: { escapeValue: false },
})

i18n.on('languageChanged', (locale) => {
  const supportedLocale = resolveSupportedLocale(locale)
  localStorage.setItem('voxnela:locale', supportedLocale)
  document.documentElement.lang = supportedLocale
  document.documentElement.dir = supportedLocale === 'ar' ? 'rtl' : 'ltr'
})

const initialLocale = resolveSupportedLocale(i18n.resolvedLanguage ?? i18n.language)
document.documentElement.lang = initialLocale
document.documentElement.dir = initialLocale === 'ar' ? 'rtl' : 'ltr'

export default i18n
